import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { useContext } from "react";
import {
  MdOutlineEdit,
  MdOutlineSort,
  MdOutlineVerticalAlignBottom,
  MdOutlineVerticalAlignTop,
} from "react-icons/md";
import { useIntl } from "react-intl";
import { useMap } from "react-map-gl/maplibre";

import {
  FileIcon,
  FolderIcon,
  LocationIcon,
  MenuAction,
  MoveMapIcon,
  TenantContext,
  TrashIcon,
} from "@nens/ranalib";
import { MoveDirection, combineUrlAndParams } from "@nens/shared-utils";

import { TAB_KEY } from "@/features/project";
import { ProjectContext } from "@/stores/project-provider";
import { convertFileIdToQueryParams } from "@/utils/convert-file-id-to-query-params";

import {
  getFileByPath,
  getFolders,
  getIsEditable,
  getIsPublic,
  getMaps,
  getOpenMap,
  getSelectedLayers,
} from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { Folder, Layer } from "../../../types/publication";
import { useLayerBounds } from "../../map-layers/hooks/use-layer-bounds";
import { hasSameOrder, reorderIds } from "../utils/reorder-utils";

type CreateMoveSubActionsProps = {
  intl: ReturnType<typeof useIntl>;
  layerIds: string[];
  moveLayersToPosition: (
    layerIds: string[],
    direction: MoveDirection,
    targetFolderId?: string
  ) => void;
  targetFolderId?: string;
  alreadyAtTop: boolean;
  alreadyAtBottom: boolean;
};

function createMoveSubActions({
  intl,
  layerIds,
  moveLayersToPosition,
  targetFolderId,
  alreadyAtTop,
  alreadyAtBottom,
}: CreateMoveSubActionsProps): MenuAction[] {
  const alreadyAtTopText = intl.formatMessage({
    id: "publication.contextmenu.move.already_at_top",
    defaultMessage: "Already at the top",
  });
  const alreadyAtBottomText = intl.formatMessage({
    id: "publication.contextmenu.move.already_at_bottom",
    defaultMessage: "Already at the bottom",
  });

  return [
    {
      label: intl.formatMessage({
        id: "publication.contextmenu.move_to_top",
        defaultMessage: "Move to top",
      }),
      icon: <MdOutlineVerticalAlignTop />,
      action: () => moveLayersToPosition(layerIds, "top", targetFolderId),
      disabledText: alreadyAtTop ? alreadyAtTopText : undefined,
    },
    {
      label: intl.formatMessage({
        id: "publication.contextmenu.move_up",
        defaultMessage: "Move up",
      }),
      icon: <ChevronUpIcon />,
      action: () => moveLayersToPosition(layerIds, "up", targetFolderId),
      disabledText: alreadyAtTop ? alreadyAtTopText : undefined,
    },
    {
      label: intl.formatMessage({
        id: "publication.contextmenu.move_down",
        defaultMessage: "Move down",
      }),
      icon: <ChevronDownIcon />,
      action: () => moveLayersToPosition(layerIds, "down", targetFolderId),
      disabledText: alreadyAtBottom ? alreadyAtBottomText : undefined,
    },
    {
      label: intl.formatMessage({
        id: "publication.contextmenu.move_to_bottom",
        defaultMessage: "Move to bottom",
      }),
      icon: <MdOutlineVerticalAlignBottom />,
      action: () => moveLayersToPosition(layerIds, "bottom", targetFolderId),
      disabledText: alreadyAtBottom ? alreadyAtBottomText : undefined,
    },
  ];
}

export function useLayerListItemActions(
  layer: Layer,
  editName: () => void,
  removeLayer: (layer: Layer) => void,
  isFirst: boolean,
  isLast: boolean,
  folder?: Folder
): MenuAction[] {
  const intl = useIntl();
  const map = useMap().current;
  const { tenant } = useContext(TenantContext);
  const isPublic = usePublicationStore(getIsPublic);
  const isEditable = usePublicationStore(getIsEditable);
  const maps = usePublicationStore(getMaps);
  const openMap = usePublicationStore(getOpenMap);
  const { bounds, boundsLoading } = useLayerBounds(layer);
  const selectedLayers = usePublicationStore(getSelectedLayers);

  const moveLayersToPosition = usePublicationStore((state) => state.moveLayersToPosition);
  const moveLayersToMap = usePublicationStore((state) => state.moveLayersToMap);
  const file = usePublicationStore(getFileByPath(layer.filePath));
  const projectContext = useContext(ProjectContext);

  const folders = usePublicationStore(getFolders);
  // Layers in the list this context menu belongs to (the folder, or the open map's root)
  const layersInScope = folder
    ? folder.layers
    : openMap.layers.filter((item) => item.type === "layer");
  const layerIdsInScope = new Set(layersInScope.map((item) => item.id));

  // Keep selection order, but only for layers in this list.
  const selectedLayerIds = selectedLayers
    .map((selectedLayer) => selectedLayer.id)
    .filter((id) => layerIdsInScope.has(id));
  const hasMultipleSelected = selectedLayerIds.length > 1;

  const selectedMoveContainer = folder ? folder.layers : openMap.layers;
  const selectedMoveContainerIds = selectedMoveContainer.map((item) => item.id);
  const isNoOpMove = (direction: "top" | "bottom") =>
    hasSameOrder(
      reorderIds(selectedMoveContainer, selectedLayerIds, direction),
      selectedMoveContainerIds
    );
  const alreadyAtTop = isNoOpMove("top");
  const alreadyAtBottom = isNoOpMove("bottom");
  const actionsForAllUsers = [
    {
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.zoom_to_layer",
        defaultMessage: "Zoom to layer",
      }),
      icon: <LocationIcon />,
      action: () => {
        if (map && bounds) {
          map.fitBounds([
            [bounds[0], bounds[1]],
            [bounds[2], bounds[3]],
          ]);
        }
      },
      disabledText:
        !boundsLoading && bounds
          ? undefined
          : intl.formatMessage({
              id: "publication.layer.contextmenu.zoom_to_layer.no_bounds",
              defaultMessage: "Zoom to layer (bounds not available)",
            }),
    },
  ];

  if (isPublic) {
    return actionsForAllUsers;
  }

  const viewFileActions: MenuAction[] = [];
  if (file) {
    const projectSlug = projectContext.slug;
    const fileId = file.file.id;

    const url = `${combineUrlAndParams(
      `${window.location.origin}/${tenant}/projects/${projectSlug}`,
      {
        [TAB_KEY]: 1,
      }
    )}&${convertFileIdToQueryParams(fileId, file.ref)}`;

    viewFileActions.push({
      icon: <FileIcon />,
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.view_file",
        defaultMessage: "View file",
      }),
      to: url,
    });
  }

  if (!isEditable) {
    return [...actionsForAllUsers, ...viewFileActions];
  }

  if (hasMultipleSelected) {
    const bulkActions: MenuAction[] = [
      {
        label: intl.formatMessage({
          id: "publication.layer.contextmenu.move_all",
          defaultMessage: "Move all",
        }),
        icon: <MdOutlineSort />,
        subActions: createMoveSubActions({
          intl,
          layerIds: selectedLayerIds,
          moveLayersToPosition,
          targetFolderId: folder?.id,
          alreadyAtTop,
          alreadyAtBottom,
        }),
      },
    ];

    if (folder) {
      bulkActions.push({
        label: intl.formatMessage({
          id: "publication.layer.contextmenu.remove_from_folder",
          defaultMessage: "Remove from folder",
        }),
        icon: <FolderIcon />,
        action: () => moveLayersToPosition(selectedLayerIds, "bottom"),
      });
    }

    if (folders.length > 0) {
      bulkActions.push({
        label: intl.formatMessage({
          id: "publication.layer.contextmenu.add_all_to_folder",
          defaultMessage: "Add all to folder",
        }),
        icon: <FolderIcon />,
        subActions: folders.map((folder) => ({
          label: folder.name,
          icon: <FolderIcon />,
          action: () => moveLayersToPosition(selectedLayerIds, "bottom", folder.id),
        })),
      });
    }

    if (maps.length > 1) {
      bulkActions.push({
        label: intl.formatMessage({
          id: "publication.layer.contextmenu.move_all_to_map",
          defaultMessage: "Move all to map",
        }),
        icon: <MoveMapIcon />,
        subActions: maps
          .filter((map) => map.id !== openMap.id)
          .map((map) => ({
            label: map.name,
            icon: <MoveMapIcon />,
            action: () => moveLayersToMap(selectedLayerIds, map.id),
          })),
      });
    }

    return bulkActions;
  }

  const actions: MenuAction[] = [
    ...actionsForAllUsers,
    {
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.rename_layer",
        defaultMessage: "Rename layer",
      }),
      icon: <MdOutlineEdit />,
      action: () => editName(),
    },
    {
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.remove_layer",
        defaultMessage: "Remove layer",
      }),
      icon: <TrashIcon />,
      action: () => removeLayer(layer),
    },
    {
      label: intl.formatMessage({
        id: "publication.contextmenu.move",
        defaultMessage: "Move",
      }),
      icon: <MdOutlineSort />,
      subActions: createMoveSubActions({
        intl,
        layerIds: [layer.id],
        moveLayersToPosition,
        targetFolderId: folder?.id,
        alreadyAtTop: isFirst,
        alreadyAtBottom: isLast,
      }),
    },
    ...viewFileActions,
  ];

  if (folder) {
    actions.push({
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.remove_from_folder",
        defaultMessage: "Remove from folder",
      }),
      icon: <FolderIcon />,
      action: () => moveLayersToPosition([layer.id], "bottom"),
    });
  } else if (folders.length > 0) {
    actions.push({
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.add_to_folder",
        defaultMessage: "Add to folder",
      }),
      icon: <FolderIcon />,
      subActions: folders.map((folder) => ({
        label: folder.name,
        icon: <FolderIcon />,
        action: () => moveLayersToPosition([layer.id], "bottom", folder.id),
      })),
    });
  }

  if (maps.length > 1) {
    actions.push({
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.move_to_map",
        defaultMessage: "Move to map",
      }),
      icon: <MoveMapIcon />,
      subActions: maps
        .filter((map) => map.id !== openMap.id)
        .map((map) => ({
          label: map.name,
          icon: <MoveMapIcon />,
          action: () => moveLayersToMap([layer.id], map.id),
        })),
    });
  }

  return actions;
}
